import React, { useState, useEffect } from "react";
import {
  Dialog,
  TextField,
  Button,
  Chip,
  Box,
  Grid,
  Typography,
  MenuItem,
} from "@mui/material";

import { useSnackbar } from "notistack";

function WardAddEditRoomDialog({
  open,
  onClose,
  onSave,
  roomName,
  setRoomName,
  roomGender,
  setRoomGender,
  beds,
  setBeds,
  editingBedIndex,
  setEditingBedIndex,
  roomID,
}) {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const [bedName, setBedName] = useState("");
  const [bedID, setBedID] = useState(null);
  const [patientID, setPatientID] = useState(null);

  const [originalRoomName, setOriginalRoomName] = useState("");
  const [originalRoomGender, setOriginalRoomGender] = useState("");
  const [originalRoomBeds, setOriginalRoomBeds] = useState([]);

  useEffect(() => {
    if (open) {
      setOriginalRoomName(roomName);
      setOriginalRoomGender(roomGender);
      setOriginalRoomBeds(beds); // Create a deep copy
    }
  }, [open]);

  const showSnackBar = (type, title, subtitle) => {
    const options = { variant: type };

    enqueueSnackbar(
      <div style={{ display: "inline-grid" }}>
        <Typography data-test={"snack-title"} variant="h6">
          {title}
        </Typography>
        <Typography data-test={"snack-text"} variant="subtitle1">
          {subtitle}
        </Typography>
      </div>,
      options
    );
  };

  const handleAddBed = () => {
    if (bedName.trim() === "") {
      return;
    }

    const newBed = {
      name: bedName,
      bed_id: bedID,
      room_id: roomID,
      patient_id: patientID,
      deleted: false,
    };

    if (editingBedIndex !== -1) {
      // Editing an existing bed
      const updatedBeds = [...beds];
      updatedBeds[editingBedIndex] = newBed;
      setBeds(updatedBeds);
      setEditingBedIndex(-1);
    } else {
      // Adding a new bed
      setBeds([...beds, newBed]);
    }

    setBedName("");
    setBedID(null);
    setPatientID(null);
  };

  const handleEditBed = (bedIndex) => {
    const bedToEdit = beds[bedIndex];
    setEditingBedIndex(bedIndex);
    setBedName(bedToEdit.name);
    setBedID(bedToEdit.bed_id);
    setPatientID(bedToEdit.patient_id);
  };

  const handleDeleteBed = (bedIndex) => {
    const updatedBeds = [...beds];
    const bedToDelete = updatedBeds[bedIndex];

    if (bedToDelete.patient_id !== null) {
      showSnackBar(
        "error",
        "Failure",
        "Bed cannot be deleted as it has a patient assigned."
      );
    } else {
      if (bedToDelete.bed_id === null) {
        updatedBeds.splice(bedIndex, 1);
      } else {
        bedToDelete.deleted = true;
      }

      setBeds(updatedBeds);
      setEditingBedIndex(-1);
    }
  };

  const resetAllStates = () => {
    setRoomName(originalRoomName);
    setRoomGender(originalRoomGender);
    setBeds(originalRoomBeds);
    setEditingBedIndex(-1);
    setBedName("");
    setBedID(null);
    setPatientID(null);
  };

  const handleClose = () => {
    resetAllStates();
    onClose();
  };

  const isNullOrEmpty = (value) => {
    if (value === null || value === undefined) {
      return true;
    }

    if (typeof value === "string" && value.trim().toLowerCase() === "null") {
      return true;
    }

    if (Array.isArray(value) && value.length === 0) {
      return true;
    }

    if (typeof value === "string" && value.trim() === "") {
      return true;
    }

    if (typeof value === "number" && isNaN(value)) {
      return true;
    }

    return false;
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <Box
        style={{
          margin: 15,
          //backgroundColor: "#FCFCFC",
        }}
      >
        <Grid
          container
          spacing={2}
          style={{ display: "flex", alignItems: "center" }}
        >
          <Grid
            item
            xs={12}
            md={12}
            style={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Typography data-test={"room-add-edit-title"} variant="h6">
              Room Details
            </Typography>
          </Grid>

          <Grid
            item
            xs={12}
            md={4}
            style={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <TextField
              data-test={"room-add-edit-name"}
              label="Room"
              variant="outlined"
              size="small"
              fullWidth
              value={roomName}
              onChange={(e) => setRoomName(e.target.value)}
            />
          </Grid>

          <Grid
            item
            xs={12}
            md={4}
            style={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <TextField
              data-test={"room-add-edit-gender"}
              id="gender"
              select
              label="Gender"
              variant="outlined"
              size="small"
              fullWidth
              value={roomGender}
              onChange={(e) => setRoomGender(e.target.value)}
            >
              <MenuItem value="male">Male</MenuItem>
              <MenuItem value="female">Female</MenuItem>
              <MenuItem value="either">Either</MenuItem>
            </TextField>
          </Grid>

          <Grid
            item
            xs={12}
            md={4}
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <TextField
              data-test={"room-add-edit-bed-name"}
              label="Bed Name"
              variant="outlined"
              size="small"
              fullWidth
              value={bedName}
              onChange={(e) => setBedName(e.target.value)}
            />
            {!isNullOrEmpty(bedName) && (
              <Button
                data-test={"room-add-edit-bed-add-btn"}
                variant="contained"
                color="primary"
                size="small"
                style={{ marginLeft: 5, padding: 7 }}
                onClick={handleAddBed}
              >
                {editingBedIndex !== -1 ? "Update" : "Add"}
              </Button>
            )}
          </Grid>

          <Grid
            item
            xs={12}
            md={12}
            style={{
              display: "flex",
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            <Typography
              data-test={"room-add-edit-subtitle"}
              variant="subtitle1"
            >
              Beds
            </Typography>
            <Typography
              data-test={"room-add-edit-subtitle-hint"}
              variant="caption"
            >
              (Hint: to edit bed name select bed from below.)
            </Typography>
          </Grid>
          <Grid
            data-test={"room-add-edit-beds-grid"}
            item
            xs={12}
            md={12}
            style={{
              display: "flex",
              justifyContent: "center",
              flexFlow: "wrap",
            }}
          >
            {beds !== undefined && beds !== null && beds.length > 0 ? (
              beds.map((bed, bedIndex) => (
                <Chip
                  key={bedIndex}
                  data-test={`room-add-edit-bed-item-${bedIndex}`}
                  color={"primary"}
                  label={bed.deleted ? <del>{bed.name}</del> : bed.name}
                  disabled={bed.deleted}
                  onDelete={() => handleDeleteBed(bedIndex)}
                  onClick={() => handleEditBed(bedIndex)}
                  style={{ margin: "4px" }}
                />
              ))
            ) : (
              <Typography data-test={"room-empty-msg"} variant="caption">
                No records to display
              </Typography>
            )}
          </Grid>
          <Grid
            item
            xs={12}
            md={12}
            style={{
              display: "flex",
              justifyContent: "flex-end",
              flexDirection: "row",
            }}
          >
            <Button
              data-test={"room-add-edit-cancel-btn"}
              variant="contained"
              color="primary"
              style={{ marginLeft: 10 }}
              onClick={handleClose}
            >
              Cancel
            </Button>

            {!isNullOrEmpty(roomName) &&
              !isNullOrEmpty(roomGender) &&
              beds !== null &&
              beds.length > 0 &&
              beds.every((bed) => bed.deleted === true) === false && (
                <Button
                  data-test={"room-add-edit-save-btn"}
                  variant="contained"
                  color="primary"
                  style={{ marginLeft: 10 }}
                  onClick={onSave}
                >
                  Save
                </Button>
              )}
          </Grid>
        </Grid>
      </Box>
    </Dialog>
  );
}

export default WardAddEditRoomDialog;
